import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useClient } from '../../../contexts/ClientContext';
import { useProject } from '../../../contexts/ProjectContext';
import { useFilter } from '../../../contexts/FilterContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { PERMISSIONS } from '../../../utils/permissions';
import {
  assignRecommendationProject,
  getCostOptimizationRecommendations,
  getCostAnalysisSummary,
  updateRecommendationStatus,
  triggerCostOptimizationScan,
  getGuardrailsEnvironments,
} from '../../../services/cloudopsApi';
import {
  FiZap,
  FiTrendingDown,
  FiDollarSign,
  FiCheckCircle,
  FiServer,
  FiAlertCircle,
  FiClock,
  FiDatabase,
  FiShield,
} from 'react-icons/fi';
import RecommendationCard from '../components/RecommendationCard';
import {
  formatCurrency,
  calculateSummary,
  getCategoryCounts,
  type Recommendation,
} from '../utils/costOptimizationHelpers';
import toast from 'react-hot-toast';

interface CostEnvironment {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface AnalysisSummary {
  executive_summary?: string;
  architectural_suggestions?: { suggestion: string; estimated_savings?: number; effort?: string }[];
  risk_warnings?: string[];
}

const CostRecommendationsPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedClientId, isLoadingClients } = useClient();
  const { selectedProjectId } = useProject();
  const { filterType, selectedId: filterId } = useFilter();
  const { hasPermission } = usePermissions();
  const { isAWSUser } = useModuleAccess();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);
  const [scanning, setScanning] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<Record<string, unknown>[]>([]);
  const [environments, setEnvironments] = useState<CostEnvironment[]>([]);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [statusFilter] = useState<string>('all');
  const [analysisSummary, setAnalysisSummary] = useState<AnalysisSummary | null>(null);

  // Load environments on mount
  useEffect(() => {
    loadEnvironments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, selectedProjectId, filterType, filterId]);

  // Load recommendations when environment is selected
  useEffect(() => {
    if (selectedEnvironmentId) {
      loadRecommendations();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvironmentId, selectedProjectId, statusFilter, filterType, filterId]);

  const loadEnvironments = async () => {
    try {
      // Avoid calling /guardrails/environments without client_id for partner/aws users.
      const needsClientId = isAWSUser() ||
        hasPermission(PERMISSIONS.CLIENTS_VIEW) ||
        hasPermission(PERMISSIONS.CLIENTS_MANAGE) ||
        hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
        hasPermission(PERMISSIONS.PARTNERS_MANAGE);
      if (needsClientId && !selectedClientId) {
        if (isLoadingClients) return;
        setError('Please select a client to view environments.');
        setLoading(false);
        return;
      }
      const clientId = selectedClientId || user?.client_id || undefined;
      const environmentsData = await getGuardrailsEnvironments(clientId);
      setEnvironments(environmentsData || []);
      
      // Auto-select environment from URL param or first available
      if ((environmentsData?.length ?? 0) > 0 && !selectedEnvironmentId) {
        const envIdFromUrl = searchParams.get('environment_id');
        const matchedEnv = envIdFromUrl && environmentsData.find((e: CostEnvironment) => e.id === envIdFromUrl);
        setSelectedEnvironmentId(matchedEnv?.id ?? environmentsData[0].id);
      } else if ((environmentsData?.length ?? 0) === 0) {
        // No environments to load recommendations for — release the loading
        // gate so the empty state can render. When envs exist, the
        // selectedEnvironmentId effect kicks off loadRecommendations which
        // owns the loading flag.
        setLoading(false);
      }
    } catch (err: unknown) {
      console.error('Error loading environments:', err);
      setError('Failed to load environments. Please ensure you have AWS environments configured.');
      setLoading(false);
    }
  };

  const loadRecommendations = async () => {
    if (!selectedEnvironmentId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const filters: Record<string, string> = {};
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }
      
      const data = await getCostOptimizationRecommendations(selectedEnvironmentId, filters);
      setRecommendations(Array.isArray(data) ? data : []);

      // Fetch account analysis summary
      try {
        const summary = await getCostAnalysisSummary(selectedEnvironmentId);
        setAnalysisSummary(summary);
      } catch {
        setAnalysisSummary(null);
      }
    } catch (err: unknown) {
      console.error('Error loading recommendations:', err);
      setError(err instanceof Error ? err.message : 'Failed to load recommendations');
      setRecommendations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (recommendationId: string, newStatus: string) => {
    setUpdating(true);
    try {
      await updateRecommendationStatus(recommendationId, newStatus);
      setRecommendations((prev) => prev.map((recommendation) => (
        recommendation.id === recommendationId ? { ...recommendation, status: newStatus } : recommendation
      )));
      toast.success(`Recommendation ${newStatus.toLowerCase()} successfully`);
    } catch (err: unknown) {
      console.error('Error updating recommendation status:', err);
      toast.error(`Failed to update recommendation: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUpdating(false);
    }
  };

  const handleProjectAssign = async (recommendationId: string, projectId: string | null) => {
    setUpdating(true);
    try {
      await assignRecommendationProject(recommendationId, projectId);
      setRecommendations((prev) => prev.map((recommendation) => (
        recommendation.id === recommendationId ? { ...recommendation, project_id: projectId } : recommendation
      )));
    } finally {
      setUpdating(false);
    }
  };

  const handleTriggerScan = async () => {
    if (!selectedEnvironmentId) {
      toast.error('Please select an environment first');
      return;
    }

    // trigger-scan endpoint expects a client_id, not environment_id
    const clientId = selectedClientId || user?.client_id;
    if (!clientId) {
      toast.error('No client context available');
      return;
    }

    setScanning(true);
    try {
      await triggerCostOptimizationScan(clientId);
      toast.success('Scan triggered successfully. Results will appear shortly.');
      
      // Reload recommendations after a delay
      setTimeout(() => {
        loadRecommendations();
      }, 5000);
    } catch (err: unknown) {
      console.error('Error triggering scan:', err);
      toast.error(`Failed to trigger scan: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setScanning(false);
    }
  };

  // Calculate summary statistics
  const summary = useMemo(() => calculateSummary(recommendations as unknown as Recommendation[]), [recommendations]);

  // Get category counts
  const categoryCounts = useMemo(() => getCategoryCounts(recommendations as unknown as Recommendation[]), [recommendations]);

  // Filter recommendations by category
  const filteredRecommendations = useMemo(() => {
    let filtered = recommendations;
    
    // Apply category filter
    if (activeCategory === 'rightsizing') {
      filtered = filtered.filter(r => r.type === 'Rightsizing');
    } else if (activeCategory === 'idleResources') {
      filtered = filtered.filter(r => r.type === 'Idle');
    } else if (activeCategory === 'reservedInstances') {
      filtered = filtered.filter(r => r.type === 'Reserved Instances');
    } else if (activeCategory === 'storageOptimization') {
      filtered = filtered.filter(r => r.type === 'Storage Optimization');
    } else if (activeCategory === 'savingsPlan') {
      filtered = filtered.filter(r => r.type === 'Savings Plan');
    }

    return filtered;
  }, [recommendations, activeCategory]);

  const categoryTabs = [
    { id: 'all', label: 'All Recommendations', icon: FiZap, count: categoryCounts.all },
    { id: 'rightsizing', label: 'Rightsizing', icon: FiServer, count: categoryCounts.rightsizing },
    { id: 'idleResources', label: 'Idle Resources', icon: FiAlertCircle, count: categoryCounts.idleResources },
    { id: 'reservedInstances', label: 'Reserved Instances', icon: FiClock, count: categoryCounts.reservedInstances },
    { id: 'storageOptimization', label: 'Storage Optimization', icon: FiDatabase, count: categoryCounts.storageOptimization },
    { id: 'savingsPlan', label: 'Savings Plans', icon: FiShield, count: categoryCounts.savingsPlan },
  ];

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <FiZap className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Cost Recommendations
              </h1>
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              AI-powered cost optimization opportunities
            </p>
          </div>
          <div className="flex gap-3">
            {/* Environment Selector */}
            {environments.length > 0 && (
              <select
                value={selectedEnvironmentId || ''}
                onChange={(e) => setSelectedEnvironmentId(e.target.value)}
                className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Environment</option>
                {environments.map((env) => (
                  <option key={env.id} value={env.id}>
                    {env.name || env.id}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={handleTriggerScan}
              disabled={scanning || !selectedEnvironmentId}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {scanning ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Scanning...
                </>
              ) : (
                <>
                  <FiZap className="w-5 h-5" />
                  Trigger Scan
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
          <button
            onClick={loadRecommendations}
            className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Summary Cards */}
      {selectedEnvironmentId && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {/* Open Recommendations */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/50 rounded-md p-3">
                  <FiZap className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Open Recommendations
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {summary.openCount}
                  </p>
                </div>
              </div>
            </div>

            {/* Monthly Savings */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-green-100 dark:bg-green-900/50 rounded-md p-3">
                  <FiTrendingDown className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Monthly Savings
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(summary.monthlySavings)}
                  </p>
                </div>
              </div>
            </div>

            {/* Annual Savings */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-purple-100 dark:bg-purple-900/50 rounded-md p-3">
                  <FiDollarSign className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Annual Savings
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(summary.annualSavings)}
                  </p>
                </div>
              </div>
            </div>

            {/* Implemented */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-green-100 dark:bg-green-900/50 rounded-md p-3">
                  <FiCheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Implemented
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {summary.implementedCount}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Account Analysis Summary */}
          {analysisSummary?.executive_summary && (
            <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                AI Account Analysis
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                {analysisSummary.executive_summary}
              </p>
              {analysisSummary.architectural_suggestions && analysisSummary.architectural_suggestions.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Architectural Suggestions</h4>
                  <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
                    {analysisSummary.architectural_suggestions.map((s: { suggestion: string; estimated_savings?: number; effort?: string }, idx: number) => (
                      <li key={idx}>
                        {s.suggestion}
                        {s.estimated_savings ? ` (est. $${s.estimated_savings}/mo savings)` : ''}
                        {s.effort ? ` — ${s.effort} effort` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {analysisSummary.risk_warnings && analysisSummary.risk_warnings.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">Risk Warnings</h4>
                  <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-300 space-y-1">
                    {analysisSummary.risk_warnings.map((w: string, idx: number) => (
                      <li key={idx}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Category Tabs */}
          <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8" aria-label="Category navigation">
              {categoryTabs.map((category) => {
                const Icon = category.icon;
                const isActive = activeCategory === category.id;
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`${
                      isActive
                        ? 'border-blue-500 text-gray-900 dark:text-white'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center transition-colors`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {category.label}
                    {category.count > 0 && (
                      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                        isActive
                          ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {category.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Recommendations List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : filteredRecommendations.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
              <FiZap className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No recommendations found
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Try triggering a scan to discover cost optimization opportunities.
              </p>
              <button
                onClick={handleTriggerScan}
                disabled={scanning || !selectedEnvironmentId}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Trigger Scan
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRecommendations.map((recommendation) => (
                <RecommendationCard
                  key={recommendation.id as string}
                  recommendation={recommendation as unknown as Parameters<typeof RecommendationCard>[0]['recommendation']}
                  onStatusUpdate={handleStatusUpdate}
                  onProjectAssign={handleProjectAssign}
                  updating={updating}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Loading state for the initial fetch — before we know if an
          environment is configured. Without this the "No AWS Environments
          Configured" panel below flashes during the fetch even when
          environments do exist. */}
      {loading && !selectedEnvironmentId && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* No Environment Selected */}
      {!loading && !selectedEnvironmentId && environments.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
          <FiServer className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No AWS Environments Configured
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Please configure an AWS environment to view cost optimization recommendations.
          </p>
          <button
            onClick={() => globalThis.location.href = '/hub/aws-environments'}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Configure AWS Environments
          </button>
        </div>
      )}
    </div>
  );
};

export default CostRecommendationsPage;
